import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import {
  localizeSegmentationValue,
  segmentationType
} from '@bingads-webui-campaign/segmentation-utilities';

import {
  DEVICE_TYPE,
} from './consts';

// value may be "2020-07-09" or "2018-07-19T00:00:00"
function localizeCategory(i18n, value, type = segmentationType.DeviceType) {
  if (type === 'Hour') {
    return i18n.formatDate(
      i18n.parseDate(value, { raw: "yyyy-MM-dd'T'HH:mm:ss" }) || i18n.parseDate(value, { raw: 'yyyy-MM-dd' }),
      { skeleton: 'yMEdh' }
    );
  } else if (type === 'Day') {
    return i18n.formatDate(
      i18n.parseDate(value, { raw: "yyyy-MM-dd'T'HH:mm:ss" }) || i18n.parseDate(value, { raw: 'yyyy-MM-dd' }),
      { skeleton: 'yMEd' }
    );
  }

  return localizeSegmentationValue({
    i18n,
    type,
    value,
  });
}

function getAllCategories(primarySegmentType, items) {
  // return harded coded list for HourOfDay and DayOfWeek
  if (_.isNull(primarySegmentType)) {
    return [];
  } else if (primarySegmentType === segmentationType.HourOfDay) {
    return _.map(_.range(24), hour => `${hour}`);
  } else if (primarySegmentType === segmentationType.DayOfWeek) {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  } else if (primarySegmentType === segmentationType.Device) {
    return _.compact(_.values(DEVICE_TYPE));
  }

  // Generate list based on union of all categories.
  // sortBy is the same sort used by auctionInsights, so categories are in order
  return _.chain(items)
    .pluck('segmentData')
    .flatten()
    .pluck('primarySegment')
    .union()
    .sortBy()
    .value();
}

export const WithCategories = (WrappedComponent) => {
  const HoC = props => (
    <WrappedComponent
      {...props}
      categories={getAllCategories(
        props.primarySegmentType,
        props.items
      )}
      localizeCategory={
        _.memoize(item =>
          localizeCategory(props.i18n, item, props.primarySegmentType))
      }
    />
  );

  HoC.displayName = `WithCategories(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    primarySegmentType: PropTypes.string,
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number),
      segmentData: PropTypes.arrayOf(PropTypes.shape({
        primarySegment: PropTypes.string,
        secondarySegment: PropTypes.string,
        value: PropTypes.number,
      })),
    })).isRequired,
  };
  HoC.defaultProps = {
    primarySegmentType: null,
  };

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};
